/*
 * WorkAndBuhin.vue の部品タブ、部品種別、極端部品・おすすめ部品表示切替を切り出した処理。
 */
import { BuhinCategory, BuhinDetail, SubBuhinCategory } from "@/model/Buhin";
import { applyDiscountShowPriceToContent } from "@/components/work-buhin/WorkBuhinDisplay";
import { isJunseiBuhinCode } from "@/components/work-buhin/WorkBuhinMatch";

import { isEmptyManualRecommendWork } from "@/components/work-buhin/WorkBuhinWorkSelection";

export interface BuhinTabSelectionState {
  activeBuhinCateIndex: number;
  activeBuhinShubetsuId: number;
  activeCateBuhins: SubBuhinCategory[];
}

export interface BuhinTabApplyActionState extends BuhinTabSelectionState {
  shouldApply: boolean;
  buhinContent: SubBuhinCategory[];
  shouldFilterManualBuhin: boolean;
  shouldMapBuhin: boolean;
}

export interface BuhinTabApplyEffectState extends BuhinTabSelectionState {
  shouldApply: boolean;
  buhinContent: SubBuhinCategory[];
  shouldFilterManualBuhin: boolean;
  shouldMapBuhin: boolean;
}

export interface ExtremeBuhinTabState {
  activeBuhinCateIndex: number;
  activeCateBuhins: SubBuhinCategory[];
  listManualBuhinFiltered: BuhinDetail[];
  activeBuhinShubetsuId: number;
  buhinContent: SubBuhinCategory[];
}

export interface ExtremeTabApplyActionState extends ExtremeBuhinTabState {
  shouldFilterManualBuhin: boolean;
}

export interface ExtremeTabApplyEffectState extends ExtremeBuhinTabState {
  shouldFilterManualBuhin: boolean;
  shouldApplyBuhinContent: boolean;
}

export interface ExtremeBuhinMasterState {
  listExtreme: BuhinDetail[];
  hasExtremeMaster: boolean;
}

export interface OsusumeBuhinTabState {
  activeBuhinCateIndex: number;
  activeCateBuhins: SubBuhinCategory[];
  items: BuhinDetail[];
  buhinContent: SubBuhinCategory[];
  selectedBuhinToAppend: BuhinDetail[];
}

export interface OsusumeTabApplyActionState extends OsusumeBuhinTabState {
  shouldApplyBuhinContent: boolean;
  fallback: OsusumeEmptyFallbackDecision;
}

export interface OsusumeTabApplyEffectState extends OsusumeBuhinTabState {
  shouldApplyActiveCateBuhins: boolean;
  shouldAppendSelectedBuhin: boolean;
  selectedBuhinToAppend: BuhinDetail[];
  shouldApplyBuhinContent: boolean;
  shouldSelectExtremeTab: boolean;
  shouldSelectBuhinShubetsu: boolean;
  fallbackBuhinShubetsuId: number | null;
  shouldApplyFallbackBuhinContent: boolean;
  shouldClearManualBuhinFiltered: boolean;
  listManualBuhinFiltered: BuhinDetail[];
  shouldFilterManualBuhin: boolean;
}

export interface OsusumeEmptyFallbackDecision {
  type: "extreme" | "selectBuhinShubetsu" | "empty";
  buhinShubetsuId: number | null;
}

export interface BuhinShubetsuDefaultState {
  defualtBuhinShubetsuCd: string;
  defualtBuhinShubetsuIndex: number;
  defualtBuhinBunruiCdList: string[];
}

export interface SelectBuhinShubetsuFallbackDecision {
  type: "osusume" | "extreme" | "manualDefaultTab" | "empty";
  osusumeBuhinList: BuhinDetail[];
}

export interface SelectBuhinShubetsuApplyActionState {
  shouldApply: boolean;
  shouldSetDefaultState: boolean;
  defaultState: BuhinShubetsuDefaultState;
  shouldSelectBuhinTab: boolean;
  selectBuhinTabIndex: number;
  shouldApplyActiveCateBuhins: boolean;
  activeCateBuhins: SubBuhinCategory[];
  shouldMapBuhin: boolean;
  fallback: SelectBuhinShubetsuFallbackDecision;
  shouldSetOsusumeBuhinList: boolean;
  shouldSelectOsusumeTab: boolean;
  shouldSelectExtremeTab: boolean;
  shouldSelectManualDefaultTab: boolean;
  shouldApplyEmptyState: boolean;
}

export interface SelectBuhinShubetsuApplyEffectState {
  shouldApply: boolean;
  shouldSetDefaultState: boolean;
  defaultState: BuhinShubetsuDefaultState;
  shouldSelectBuhinTab: boolean;
  selectBuhinTabIndex: number;
  shouldApplyActiveCateBuhins: boolean;
  activeCateBuhins: SubBuhinCategory[];
  shouldMapBuhin: boolean;
  shouldSetOsusumeBuhinList: boolean;
  osusumeBuhinList: BuhinDetail[];
  shouldSelectOsusumeTab: boolean;
  shouldSelectExtremeTab: boolean;
  shouldSelectManualDefaultTab: boolean;
  manualDefaultTabIndex: number;
  shouldApplyEmptyState: boolean;
  emptyActiveBuhinCateIndex: number;
  emptyDefualtBuhinShubetsuIndex: number;
  emptySelFilter: string[];
  emptyBuhinContent: SubBuhinCategory[];
  emptyActiveCateBuhins: SubBuhinCategory[];
}

export interface ManualBuhinFilterState {
  listManualBuhinFiltered: BuhinDetail[];
}

export interface ManualBuhinFilterApplyEffectState {
  listManualBuhinFiltered: BuhinDetail[];
}

export interface ManualBuhinFilterCommitApplyEffectState {
  listManualBuhinFiltered: BuhinDetail[];
}

export interface BuhinTabDisplayState {
  shouldShowJunseiTab: boolean;
  shouldShowOsusumeTab: boolean;
  shouldShowJunseiSearchButton: boolean;
  shouldShowSelectAllOsusume: boolean;
  extremeTabLabel: string;
}

export interface BuhinTabClassState {
  classes: {
    gray: boolean;
    green: boolean;
  };
}

/*
 * buildBuhinTabSelectionState を WorkAndBuhin.vue から分離し、
 * 部品タブ表示状態 を親側置換や単体テストで固定するため。
 */
export function buildBuhinTabSelectionState(
  index: number,
  buhinCategory: BuhinCategory[]
): BuhinTabSelectionState {
  const category = buhinCategory[index];
  return {
    activeBuhinCateIndex: index,
    activeBuhinShubetsuId: category && category.buhinShubetsuId
      ? category.buhinShubetsuId
      : 0,
    activeCateBuhins: category ? category.items : []
  };
}

/*
 * buildBuhinTabApplyEffectState を WorkAndBuhin.vue から分離し、
 * 通常部品タブ選択後に component へ反映する state と後続処理条件を固定するため。
 */
export function buildBuhinTabApplyEffectState(
  tabState: BuhinTabApplyActionState
): BuhinTabApplyEffectState {
  return {
    shouldApply: tabState.shouldApply,
    activeBuhinCateIndex: tabState.activeBuhinCateIndex,
    activeBuhinShubetsuId: tabState.activeBuhinShubetsuId,
    activeCateBuhins: tabState.activeCateBuhins,
    buhinContent: tabState.buhinContent,
    shouldFilterManualBuhin: tabState.shouldFilterManualBuhin,
    shouldMapBuhin: tabState.shouldMapBuhin
  };
}

/*
 * buildBuhinTabApplyActionState を WorkAndBuhin.vue から分離し、
 * 通常部品タブ選択時の state 反映値と後続処理の実行条件を固定するため。
 */
export function buildBuhinTabApplyActionState(params: {
  index: number;
  activeBuhinCateIndex: number;
  buhinCategory: BuhinCategory[];
  freeOfChargeBuhinList: BuhinDetail[];
}): BuhinTabApplyActionState {
  const shouldApply = params.index != params.activeBuhinCateIndex;
  if (!shouldApply) {
    return {
      activeBuhinCateIndex: params.activeBuhinCateIndex,
      activeBuhinShubetsuId: 0,
      activeCateBuhins: [],
      shouldApply: false,
      buhinContent: [],
      shouldFilterManualBuhin: false,
      shouldMapBuhin: false
    };
  }
  const tabState = buildBuhinTabSelectionState(
    params.index,
    params.buhinCategory
  );
  const buhinContent = applyDiscountShowPriceToContent(
    tabState.activeCateBuhins,
    params.freeOfChargeBuhinList
  );
  return {
    ...tabState,
    activeCateBuhins: buhinContent,
    shouldApply: true,
    buhinContent,
    shouldFilterManualBuhin: true,
    shouldMapBuhin: true
  };
}

/*
 * buildBuhinShubetsuDefaultState を WorkAndBuhin.vue から分離し、
 * 部品タブ表示状態 を親側置換や単体テストで固定するため。
 */
export function buildBuhinShubetsuDefaultState(
  buhinShubetsuCd: string,
  buhinBunruiList: any,
  buhinCategory: BuhinCategory[]
): BuhinShubetsuDefaultState {
  const shubetsuIndex = buhinCategory.findIndex(
    (cate: BuhinCategory) => cate.buhinShubetsuCd == buhinShubetsuCd
  );
  const defualtBuhinBunruiCdList = buhinBunruiList && buhinBunruiList.length > 0
    ? buhinBunruiList.map((bunrui: any) => bunrui.buhinBunruiCd)
    : [];
  return {
    defualtBuhinShubetsuCd: buhinShubetsuCd,
    defualtBuhinShubetsuIndex: shubetsuIndex,
    defualtBuhinBunruiCdList
  };
}

/*
 * resolveSelectBuhinShubetsuFallback を WorkAndBuhin.vue から分離し、
 * 部品タブ表示状態 を親側置換や単体テストで固定するため。
 */
export function resolveSelectBuhinShubetsuFallback(
  data: any,
  isManualWork: boolean
): SelectBuhinShubetsuFallbackDecision {
  if (data.osusumeBuhinList && data.osusumeBuhinList.length > 0) {
    return {
      type: "osusume",
      osusumeBuhinList: data.osusumeBuhinList
    };
  }
  if (data.isExtreme) {
    return {
      type: "extreme",
      osusumeBuhinList: []
    };
  }
  if (isManualWork || isEmptyManualRecommendWork(data)) {
    return {
      type: "manualDefaultTab",
      osusumeBuhinList: []
    };
  }
  return {
    type: "empty",
    osusumeBuhinList: []
  };
}

/*
 * buildSelectBuhinShubetsuApplyActionState を WorkAndBuhin.vue から分離し、
 * 部品種別タブ選択時の通常タブ再選択、同一タブ再描画、fallback action を固定するため。
 */
export function buildSelectBuhinShubetsuApplyActionState(params: {
  hasBuhinData: boolean;
  buhinShubetsuCd: string;
  buhinBunruiList: any;
  buhinCategory: BuhinCategory[];
  activeBuhinCateIndex: number;
  data: any;
  isManualWork: boolean;
  freeOfChargeBuhinList: BuhinDetail[];
}): SelectBuhinShubetsuApplyActionState {
  const emptyDefaultState: BuhinShubetsuDefaultState = {
    defualtBuhinShubetsuCd: "",
    defualtBuhinShubetsuIndex: -1,
    defualtBuhinBunruiCdList: []
  };
  const emptyFallback: SelectBuhinShubetsuFallbackDecision = {
    type: "empty",
    osusumeBuhinList: []
  };
  const baseState: SelectBuhinShubetsuApplyActionState = {
    shouldApply: params.hasBuhinData,
    shouldSetDefaultState: false,
    defaultState: emptyDefaultState,
    shouldSelectBuhinTab: false,
    selectBuhinTabIndex: -1,
    shouldApplyActiveCateBuhins: false,
    activeCateBuhins: [],
    shouldMapBuhin: false,
    fallback: emptyFallback,
    shouldSetOsusumeBuhinList: false,
    shouldSelectOsusumeTab: false,
    shouldSelectExtremeTab: false,
    shouldSelectManualDefaultTab: false,
    shouldApplyEmptyState: false
  };
  if (!params.hasBuhinData) {
    return baseState;
  }
  if (params.buhinShubetsuCd) {
    const defaultState = buildBuhinShubetsuDefaultState(
      params.buhinShubetsuCd,
      params.buhinBunruiList,
      params.buhinCategory
    );
    const shouldSelectBuhinTab = params.activeBuhinCateIndex !=
      defaultState.defualtBuhinShubetsuIndex;
    const activeCategory = params.buhinCategory[params.activeBuhinCateIndex];
    const activeCateBuhins = !shouldSelectBuhinTab && activeCategory
      ? applyDiscountShowPriceToContent(
        activeCategory.items,
        params.freeOfChargeBuhinList
      )
      : [];
    return {
      ...baseState,
      shouldSetDefaultState: true,
      defaultState,
      shouldSelectBuhinTab,
      selectBuhinTabIndex: defaultState.defualtBuhinShubetsuIndex,
      shouldApplyActiveCateBuhins: !shouldSelectBuhinTab,
      activeCateBuhins,
      shouldMapBuhin: !shouldSelectBuhinTab
    };
  }
  const fallback = resolveSelectBuhinShubetsuFallback(
    params.data,
    params.isManualWork
  );
  return {
    ...baseState,
    fallback,
    shouldSetOsusumeBuhinList: fallback.type === "osusume",
    shouldSelectOsusumeTab: fallback.type === "osusume",
    shouldSelectExtremeTab: fallback.type === "extreme",
    shouldSelectManualDefaultTab: fallback.type === "manualDefaultTab",
    shouldApplyEmptyState: fallback.type === "empty"
  };
}

/*
 * buildSelectBuhinShubetsuApplyEffectState を WorkAndBuhin.vue から分離し、
 * 部品種別選択後に component で実行する state 反映と fallback 副作用を固定するため。
 */
export function buildSelectBuhinShubetsuApplyEffectState(
  actionState: SelectBuhinShubetsuApplyActionState
): SelectBuhinShubetsuApplyEffectState {
  return {
    shouldApply: actionState.shouldApply,
    shouldSetDefaultState: actionState.shouldSetDefaultState,
    defaultState: actionState.defaultState,
    shouldSelectBuhinTab: actionState.shouldSelectBuhinTab,
    selectBuhinTabIndex: actionState.selectBuhinTabIndex,
    shouldApplyActiveCateBuhins: actionState.shouldApplyActiveCateBuhins,
    activeCateBuhins: actionState.activeCateBuhins,
    shouldMapBuhin: actionState.shouldMapBuhin,
    shouldSetOsusumeBuhinList: actionState.shouldSetOsusumeBuhinList,
    osusumeBuhinList: actionState.fallback.osusumeBuhinList,
    shouldSelectOsusumeTab: actionState.shouldSelectOsusumeTab,
    shouldSelectExtremeTab: actionState.shouldSelectExtremeTab,
    shouldSelectManualDefaultTab: actionState.shouldSelectManualDefaultTab,
    manualDefaultTabIndex: 6,
    shouldApplyEmptyState: actionState.shouldApplyEmptyState,
    emptyActiveBuhinCateIndex: -1,
    emptyDefualtBuhinShubetsuIndex: -1,
    emptySelFilter: [],
    emptyBuhinContent: [],
    emptyActiveCateBuhins: []
  };
}

/*
 * getExtremeBuhinMaster を WorkAndBuhin.vue から分離し、
 * 部品タブ表示状態 を親側置換や単体テストで固定するため。
 */
export function getExtremeBuhinMaster(listBuhinActiveSeibi: BuhinDetail[]): BuhinDetail[] {
  return listBuhinActiveSeibi.filter(
    (buhin: BuhinDetail) => buhin.buhinId == null && buhin.isExtreme == 1
  );
}

/*
 * buildExtremeBuhinMasterState を WorkAndBuhin.vue から分離し、
 * 極端部品マスタの list と有無判定を同じ条件で固定するため。
 */
export function buildExtremeBuhinMasterState(
  listBuhinActiveSebi: BuhinDetail[]
): ExtremeBuhinMasterState {
  const listExtreme = getExtremeBuhinMaster(listBuhinActiveSebi);
  return {
    listExtreme,
    hasExtremeMaster: listExtreme.length > 0
  };
}

/*
 * buildExtremeTabState を WorkAndBuhin.vue から分離し、
 * 部品タブ表示状態 を親側置換や単体テストで固定するため。
 */
export function buildExtremeTabState(
  listBuhinActiveSeibi: BuhinDetail[]
): ExtremeBuhinTabState {
  const listExtMaster = getExtremeBuhinMaster(listBuhinActiveSeibi);
  const buhinContent = listBuhinActiveSeibi.length > 0 && listExtMaster.length > 0
    ? [{
      buhinBunruiCd: "",
      buhinBunruiId: "",
      buhinBunruiNm: "",
      items: listExtMaster
    }]
    : [];

  return {
    activeBuhinCateIndex: -2,
    activeCateBuhins: [],
    listManualBuhinFiltered: [],
    activeBuhinShubetsuId: 0,
    buhinContent
  };
}

/*
 * buildExtremeTabApplyActionState を WorkAndBuhin.vue から分離し、
 * 極端部品タブ選択時の state 反映値と手入力部品 filter 再実行要否を固定するため。
 */
export function buildExtremeTabApplyActionState(
  listBuhinActiveSebi: BuhinDetail[]
): ExtremeTabApplyActionState {
  return {
    ...buildExtremeTabState(listBuhinActiveSebi),
    shouldFilterManualBuhin: true
  };
}

/*
 * buildExtremeTabApplyEffectState を WorkAndBuhin.vue から分離し、
 * 極端部品タブ選択後に component へ反映する state と filter 再実行条件を固定するため。
 */
export function buildExtremeTabApplyEffectState(
  tabState: ExtremeTabApplyActionState
): ExtremeTabApplyEffectState {
  return {
    activeBuhinCateIndex: tabState.activeBuhinCateIndex,
    activeCateBuhins: tabState.activeCateBuhins,
    listManualBuhinFiltered: tabState.listManualBuhinFiltered,
    activeBuhinShubetsuId: tabState.activeBuhinShubetsuId,
    buhinContent: tabState.buhinContent,
    shouldFilterManualBuhin: tabState.shouldFilterManualBuhin,
    shouldApplyBuhinContent: true
  };
}

/*
 * buildManualBuhinFiltered を WorkAndBuhin.vue から分離し、
 * 部品タブ表示状態 を親側置換や単体テストで固定するため。
 */
export function buildManualBuhinFiltered(
  listManualBuhin: BuhinDetail[],
  activeBuhinCateIndex: number,
  buhinCategory: BuhinCategory[]
): BuhinDetail[] {
  if (listManualBuhin && activeBuhinCateIndex >= 0) {
    let buhinShubetsuId = -1;
    if (buhinCategory[activeBuhinCateIndex]) {
      buhinShubetsuId = buhinCategory[activeBuhinCateIndex].buhinShubetsuId || -1;
    }
    return listManualBuhin.filter(
      (buhin: BuhinDetail) => buhinShubetsuId == buhin.buhinShubetsuId
    );
  }
  if (listManualBuhin && activeBuhinCateIndex === -4) {
    return listManualBuhin.filter(
      (buhin: BuhinDetail) => isJunseiBuhinCode(buhin.buhinCd) && buhin.isDelete != 1
    );
  }
  return [];
}

/*
 * buildManualBuhinFilterState を WorkAndBuhin.vue から分離し、
 * 手入力部品 filter の state 反映値を親側置換や単体テストで固定するため。
 */
export function buildManualBuhinFilterState(
  listManualBuhin: BuhinDetail[],
  activeBuhinCateIndex: number,
  buhinCategory: BuhinCategory[]
): ManualBuhinFilterState {
  return {
    listManualBuhinFiltered: buildManualBuhinFiltered(
      listManualBuhin,
      activeBuhinCateIndex,
      buhinCategory
    )
  };
}

/*
 * buildManualBuhinFilterApplyEffectState を WorkAndBuhin.vue から分離し、
 * 手入力部品 filter 後に component 側へ反映する listManualBuhinFiltered を固定するため。
 */
export function buildManualBuhinFilterApplyEffectState(
  filterState: ManualBuhinFilterState
): ManualBuhinFilterApplyEffectState {
  return {
    listManualBuhinFiltered: filterState.listManualBuhinFiltered
  };
}

/*
 * buildManualBuhinFilterCommitApplyEffectState を WorkAndBuhin.vue から分離し、
 * 手入力部品 filter 後に component が反映する listManualBuhinFiltered を固定するため。
 */
export function buildManualBuhinFilterCommitApplyEffectState(
  effectState: ManualBuhinFilterApplyEffectState
): ManualBuhinFilterCommitApplyEffectState {
  return {
    listManualBuhinFiltered: effectState.listManualBuhinFiltered
  };
}

/*
 * buildBuhinTabDisplayState を WorkAndBuhin.vue から分離し、
 * 純正・おすすめ・検索・全選択の表示条件と Extreme 表示名を固定するため。
 */
export function buildBuhinTabDisplayState(params: {
  isUseJunseiBuhin: boolean;
  osusumeBuhinList: BuhinDetail[];
  activeBuhinCateIndex: number;
  isNextageCustom: boolean;
}): BuhinTabDisplayState {
  const hasOsusumeBuhin = params.osusumeBuhinList.length > 0;
  return {
    shouldShowJunseiTab: params.isUseJunseiBuhin,
    shouldShowOsusumeTab: hasOsusumeBuhin,
    shouldShowJunseiSearchButton: params.isUseJunseiBuhin && params.activeBuhinCateIndex == -4,
    shouldShowSelectAllOsusume:
      params.activeBuhinCateIndex == -3 && hasOsusumeBuhin,
    extremeTabLabel: params.isNextageCustom ? "NICS" : "Extreme"
  };
}

/*
 * buildBuhinTabClassState を WorkAndBuhin.vue から分離し、
 * 部品タブの active index による gray / green class 条件を固定するため。
 */
export function buildBuhinTabClassState(params: {
  activeBuhinCateIndex: number;
  targetIndex: number;
}): BuhinTabClassState {
  return {
    classes: {
      gray: params.activeBuhinCateIndex != params.targetIndex,
      green: params.activeBuhinCateIndex == params.targetIndex
    }
  };
}

/*
 * buildOsusumeTabState を WorkAndBuhin.vue から分離し、
 * 部品タブ表示状態 を親側置換や単体テストで固定するため。
 */
export function buildOsusumeTabState(
  osusumeBuhinList: BuhinDetail[],
  selectedBuhinLength: number
): OsusumeBuhinTabState {
  const items = osusumeBuhinList.map((buhin: BuhinDetail) => ({
    ...buhin,
    quantity: buhin.quantity || 0
  }));
  let selectedBuhinToAppend: BuhinDetail[] = [];
  if (selectedBuhinLength == 0) {
    const selectedBuhin = items.filter(
      (buhin: BuhinDetail) => buhin.id && buhin.quantity > 0
    );
    const osusumeBuhins = items.filter((buhin: BuhinDetail) => buhin.quantity > 0);
    if (selectedBuhin && selectedBuhin.length > 0) {
      selectedBuhinToAppend = selectedBuhin;
    } else if (osusumeBuhins && osusumeBuhins.length == 1) {
      selectedBuhinToAppend = items.filter((buhin: BuhinDetail) => buhin.quantity > 0);
    }
  }
  return {
    activeBuhinCateIndex: -3,
    activeCateBuhins: [],
    items,
    buhinContent: osusumeBuhinList.length > 0
      ? [{
        buhinBunruiCd: "",
        buhinBunruiId: "",
        buhinBunruiNm: "",
        items
      }]
      : [],
    selectedBuhinToAppend
  };
}

/*
 * buildOsusumeTabApplyActionState を WorkAndBuhin.vue から分離し、
 * おすすめ部品タブ選択時の追加部品、表示反映、fallback action を固定するため。
 */
export function buildOsusumeTabApplyActionState(params: {
  osusumeBuhinList: BuhinDetail[];
  selectedBuhinLength: number;
  listExtreme: BuhinDetail[];
  isDialog: boolean;
  workBeforeChange: any;
  listSelectedBuhin: BuhinDetail[];
}): OsusumeTabApplyActionState {
  const tabState = buildOsusumeTabState(
    params.osusumeBuhinList,
    params.selectedBuhinLength
  );
  const fallback = resolveOsusumeEmptyFallback(params);
  return {
    ...tabState,
    shouldApplyBuhinContent: params.osusumeBuhinList.length > 0 ||
      fallback.type === "empty",
    fallback
  };
}

/*
 * buildOsusumeTabApplyEffectState を WorkAndBuhin.vue から分離し、
 * おすすめ部品タブ選択後の state 反映、選択部品追加、fallback 副作用条件を固定するため。
 */
export function buildOsusumeTabApplyEffectState(
  tabState: OsusumeTabApplyActionState
): OsusumeTabApplyEffectState {
  return {
    activeBuhinCateIndex: tabState.activeBuhinCateIndex,
    activeCateBuhins: tabState.activeCateBuhins,
    items: tabState.items,
    buhinContent: tabState.buhinContent,
    selectedBuhinToAppend: tabState.selectedBuhinToAppend,
    shouldApplyActiveCateBuhins: true,
    shouldAppendSelectedBuhin: tabState.selectedBuhinToAppend.length > 0,
    shouldApplyBuhinContent: tabState.shouldApplyBuhinContent,
    shouldSelectExtremeTab: !tabState.shouldApplyBuhinContent &&
      tabState.fallback.type === "extreme",
    shouldSelectBuhinShubetsu: !tabState.shouldApplyBuhinContent &&
      tabState.fallback.type === "selectBuhinShubetsu",
    fallbackBuhinShubetsuId: tabState.fallback.buhinShubetsuId,
    shouldApplyFallbackBuhinContent: !tabState.shouldApplyBuhinContent &&
      tabState.fallback.type === "empty",
    shouldClearManualBuhinFiltered: true,
    listManualBuhinFiltered: [],
    shouldFilterManualBuhin: true
  };
}

/*
 * resolveOsusumeEmptyFallback を WorkAndBuhin.vue から分離し、
 * 部品タブ表示状態 を親側置換や単体テストで固定するため。
 */
export function resolveOsusumeEmptyFallback(params: {
  listExtreme: BuhinDetail[];
  isDialog: boolean;
  workBeforeChange: any;
  listSelectedBuhin: BuhinDetail[];
}): OsusumeEmptyFallbackDecision {
  if (params.listExtreme.length > 0) {
    return {
      type: "extreme",
      buhinShubetsuId: null
    };
  }
  if (!params.isDialog && params.workBeforeChange && params.workBeforeChange.isExtreme) {
    return {
      type: "extreme",
      buhinShubetsuId: null
    };
  }
  if (params.listSelectedBuhin.length > 0) {
    return {
      type: "selectBuhinShubetsu",
      buhinShubetsuId: params.listSelectedBuhin[0].buhinShubetsuId || null
    };
  }
  return {
    type: "empty",
    buhinShubetsuId: null
  };
}
